const ALLOWED_ORIGINS = ['https://connect.evaro.com', 'https://connect-staging.evaro.com', 'https://connect-development.evaro.com', 'http://localhost:3000']

const FALLBACK_ORIGIN = 'https://connect-development.evaro.com'

// Resolves the app origin the OAuth providers redirect back to
export const getSSOOrigin = () => {
  if (typeof window === 'undefined') return ''
  const origin = window.location.origin
  return ALLOWED_ORIGINS.includes(origin) ? origin : FALLBACK_ORIGIN
}

export const getGoogleRedirectURI = () => (getSSOOrigin() ? `${getSSOOrigin()}/auth/google/callback` : '')

// Where Apple is allowed to send the result; a preview deployment or localhost is not registered with it, so
// those fall back to the development origin. Redirect URI and return URL are checked against each other at the
// callback, so everything Apple-facing must agree on this one answer.
export const getAppleOrigin = (origin: string): string => (origin.startsWith('https://') && ALLOWED_ORIGINS.includes(origin) ? origin : FALLBACK_ORIGIN)

export const getAppleRedirectURI = () => {
  if (typeof window === 'undefined') return ''
  // No trailing slash: Next.js 308s that form, and Apple's cross-site form POST arrives through the
  // cacheable redirect with no body, which reads as a failed sign-in.
  return `${getAppleOrigin(window.location.origin)}/auth/apple/callback`
}

// The state travels to Apple and comes back in a form POST, where "+" decodes to a
// space. base64url avoids the characters that can be altered on that trip.
export const toBase64Url = (value: string) => btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')

// Reads base64url and plain base64 alike, so a state built by an older iframe.js still decodes. The space is
// not cosmetic: a form body decodes plain base64's "+" as a space, so it has to be put back or every byte
// after it shifts.
export const fromBase64Url = (value: string) => {
  const normalised = value.replace(/ /g, '+').replace(/-/g, '+').replace(/_/g, '/')
  return atob(normalised.padEnd(Math.ceil(normalised.length / 4) * 4, '='))
}

export const isAllowedRedirectURI = (uri: string) => ALLOWED_ORIGINS.some(origin => uri === `${origin}/auth/google/callback`)

export const createSSOState = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

export const isEmbedded = () => typeof window !== 'undefined' && window.self !== window.top

// Safari (desktop or iOS), excluding Chrome/Edge/Firefox on iOS, which also carry "Safari" in the UA. Its
// native Apple sign-in sheet cannot deliver a result back to an embedded iframe, hence the redirect flow.
export const isSafari = () => {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return /safari/i.test(ua) && !/chrome|chromium|crios|edg|android|fxios/i.test(ua)
}

// iOS, including iPadOS 13+, which reports a desktop Mac UA and is only distinguishable by touch support.
// Every iOS browser is WebKit, so Chrome and Firefox there get the same sheet isSafari() excludes.
export const isIOS = () => {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  if (/ipad|iphone|ipod/i.test(ua)) return true
  return /macintosh/i.test(ua) && (navigator.maxTouchPoints || 0) > 1
}

// Browsers whose Apple sign-in runs through the native OS sheet: it cannot answer through window.opener, and
// on iOS the "popup" is a tab switch that lets the opener be discarded.
export const shouldUseAppleRedirect = () => isSafari() || isIOS()

// A vendor domain or configured origin reduced to the bare hostname used for trust matching. Null for
// anything empty, unparseable, or a bare TLD with no dot, which would over-match.
export const normalizeTrustedHost = (value: string): string | null => {
  const trimmed = (value || '').trim()
  if (!trimmed) return null
  let host: string
  try {
    host = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`).hostname
  } catch {
    return null
  }
  host = host.replace(/^www\./i, '')
  const isLocal = host === 'localhost' || host === '127.0.0.1'
  if (!isLocal && !host.includes('.')) return null
  return host || null
}

// True when `origin` is one of the trusted embedder hosts or a subdomain of one,
// served over https (localhost allowed over http for local development). Trusted
// hosts come from the vendor registry (services/server/ssoOrigins); a subdomain of
// a vendor's domain is only reachable by that vendor, so www/dev/staging embeds are
// covered without listing each variant. Pure so it runs server- and client-side.
export const isTrustedOrigin = (origin: string, trustedHosts: string[]): boolean => {
  let url: URL
  try {
    url = new URL(origin)
  } catch {
    return false
  }
  if (url.origin !== origin) return false
  const host = url.hostname
  const isLocal = host === 'localhost' || host === '127.0.0.1'
  if (url.protocol !== 'https:' && !isLocal) return false
  return trustedHosts.some(trusted => host === trusted || host.endsWith(`.${trusted}`))
}

// Resolves a full return URL carried in the redirect-flow state against the trusted
// hosts, so the callback can't be turned into an open redirect / token leak.
export const resolveTrustedReturnUrl = (rawUrl: string, trustedHosts: string[]): string | null => {
  try {
    const url = new URL(rawUrl)
    return isTrustedOrigin(url.origin, trustedHosts) ? url.toString() : null
  } catch {
    return null
  }
}

// Splits "nonce|base64(origin)" state and resolves the origin against the trusted
// hosts. Returns target=null for standalone or an untrusted/forged origin, so the
// caller falls back to posting to its own origin instead of the attacker's.
export const resolveSSOTarget = (rawState: string, trustedHosts: string[]): { state: string; target: string | null } => {
  const sep = rawState.indexOf('|')
  if (sep < 0) return { state: rawState, target: null }
  const state = rawState.slice(0, sep)
  try {
    const origin = fromBase64Url(rawState.slice(sep + 1))
    return { state, target: isTrustedOrigin(origin, trustedHosts) ? origin : null }
  } catch {
    return { state, target: null }
  }
}

// The Apple callback state is either the popup form "nonce|base64(origin)" or the
// Safari redirect form "nonce|R|base64(returnUrl)". Distinguishes and resolves both.
export type AppleCallbackState =
  | { mode: 'redirect'; nonce: string; returnUrl: string | null; rejectedOrigin: string | null }
  | { mode: 'popup'; nonce: string; target: string | null }

export const parseAppleCallbackState = (rawState: string, trustedHosts: string[]): AppleCallbackState => {
  const parts = rawState.split('|')
  const nonce = parts[0] || ''
  if (parts[1] === 'R') {
    let returnUrl: string | null = null
    // Carried out of here so a refusal can be logged by origin. Reading the wire
    // format a second time at the call site would leave two copies to keep in step.
    let rejectedOrigin: string | null = null
    try {
      const decoded = fromBase64Url(parts[2] || '')
      returnUrl = resolveTrustedReturnUrl(decoded, trustedHosts)
      if (!returnUrl) {
        rejectedOrigin = new URL(decoded).origin
      }
    } catch {
      returnUrl = null
      rejectedOrigin = rejectedOrigin ?? 'unreadable'
    }
    return { mode: 'redirect', nonce, returnUrl, rejectedOrigin }
  }
  const { state, target } = resolveSSOTarget(rawState, trustedHosts)
  return { mode: 'popup', nonce: state, target }
}

// Escapes a value for safe embedding inside an inline <script>: < (tag/comment)
// and the U+2028/U+2029 line separators, valid in JSON but not in JS strings.
export const escapeForScript = (value: unknown) =>
  JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029')

// The embedding host's origin, derived from the referrer of the initial load
export const getHostOrigin = (): string | null => {
  if (typeof document === 'undefined' || !document.referrer) return null
  try {
    return new URL(document.referrer).origin
  } catch {
    return null
  }
}

// Guards a relayed SSO message. Standalone: must be from our same-origin popup.
// Embedded: must come from the parent window and, when the host origin is known,
// match it. The single-use state nonce is validated separately by the consumer.
export const isTrustedSSOMessage = (event: MessageEvent): boolean => {
  if (isEmbedded()) {
    if (event.source !== window.parent) return false
    const hostOrigin = getHostOrigin()
    return !hostOrigin || event.origin === hostOrigin
  }
  return event.origin === window.location.origin
}

// Opens a centered auth popup; must be called synchronously from a click handler
export const openSSOPopup = (url: string, name: string) => {
  const width = 500
  const height = 600
  const left = Math.max(0, Math.round((window.screen.width - width) / 2))
  const top = Math.max(0, Math.round((window.screen.height - height) / 2))
  return window.open(url, name, `popup=yes,width=${width},height=${height},left=${left},top=${top}`)
}

// Starts the auth popup. When embedded in a host page, the host (top window) opens
// it — Safari crashes if a cross-origin iframe opens the auth popup itself. Returns
// 'delegated' when the host opens it, or the popup Window (or null if blocked).
export const startAuthPopup = (url: string, name: string): Window | null | 'delegated' => {
  if (isEmbedded()) {
    window.parent.postMessage({ type: 'EVARO_SSO_OPEN', data: { url } }, '*')
    return 'delegated'
  }
  return openSSOPopup(url, name)
}

// sessionStorage throws in iframes whose storage access is blocked, so every access
// is guarded and degrades to a no-op.
const readSession = (key: string): string | null => {
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

const writeSession = (key: string, value: string) => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(key, value)
  } catch {}
}

const clearSession = (key: string) => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(key)
  } catch {}
}

// Named on both sides of the redirect: the callback writes them into the fragment
// and the return leg reads them back.
export const APPLE_CODE_PARAM = 'evaro_apple'
export const APPLE_ERROR_PARAM = 'evaro_apple_error'
// Set by a host page holding a returned code, which it relays once Connect is ready.
export const APPLE_PENDING_PARAM = 'evaro_apple_pending'

const PENDING_STATE_KEY = 'evaro_apple_state'
const REDIRECT_MARKER_KEY = 'evaro_apple_redirect'
const RETURN_TO_KEY = 'evaro_apple_return_to'

// Pending state nonce shared between the initiating hook and the message listener.
// It is mirrored into sessionStorage because the tab can be discarded while the
// native Apple sheet is up, and kept in memory too for the cases where storage is
// unavailable. Losing it makes the listener drop an otherwise valid sign-in.
let pendingAppleState: string | null = null

export const setPendingAppleState = (state: string) => {
  pendingAppleState = state
  writeSession(PENDING_STATE_KEY, state)
}

export const peekPendingAppleState = () => pendingAppleState ?? readSession(PENDING_STATE_KEY)

export const consumePendingAppleState = () => {
  const state = peekPendingAppleState()
  pendingAppleState = null
  clearSession(PENDING_STATE_KEY)
  return state
}

// Where the patient was heading when they were asked to sign in. The callback that
// would have taken them there is a function in memory, and the trip to Apple reloads
// the page, so the destination travels as a string instead.
export const setPendingAppleReturnTo = (path: string) => writeSession(RETURN_TO_KEY, path)

export const consumePendingAppleReturnTo = () => {
  const path = readSession(RETURN_TO_KEY)
  clearSession(RETURN_TO_KEY)
  return path
}

// Marks that we are leaving for Apple, so the return leg can tell a redirect
// return apart from an ordinary page load. Read once and cleared, so the reload
// it guards can only ever happen once.
export const markAppleRedirectPending = () => writeSession(REDIRECT_MARKER_KEY, '1')

export const consumeAppleRedirectMarker = () => {
  const marker = readSession(REDIRECT_MARKER_KEY) === '1'
  clearSession(REDIRECT_MARKER_KEY)
  return marker
}

// A back or forward step can land here with the marker still set, without a sign-in to finish.
export const isBackForwardNavigation = () => {
  try {
    const [entry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[]
    return entry?.type === 'back_forward'
  } catch {
    return false
  }
}

export type AppleReturnLeg = { kind: 'code' | 'error' | 'relay' | 'reload' | 'none'; value?: string }

// What this page load has to do to finish an Apple redirect sign-in, from its fragment and
// from whether this tab left for Apple. A relay needs an embed, since only a host page sends one.
export const readAppleReturnLeg = (hash: URLSearchParams, leftForApple: boolean): AppleReturnLeg => {
  const code = hash.get(APPLE_CODE_PARAM)
  if (code) return { kind: 'code', value: code }
  const error = hash.get(APPLE_ERROR_PARAM)
  if (error) return { kind: 'error', value: error }
  if (hash.has(APPLE_PENDING_PARAM) && isEmbedded()) return { kind: 'relay' }
  // Link tracking prevention can hide the fragment until a same-site reload re-reads the URL
  if (leftForApple && !isBackForwardNavigation()) return { kind: 'reload' }
  return { kind: 'none' }
}

// Whether this page load still has an Apple sign-in to finish. An error return has not,
// so a login modal the page opens can show the error.
export const isReturningFromApple = () => {
  if (typeof window === 'undefined') return false
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))
  const { kind } = readAppleReturnLeg(hash, readSession(REDIRECT_MARKER_KEY) === '1')
  return kind === 'code' || kind === 'relay' || kind === 'reload'
}

// The page to come back to after Apple. The fragment is cleared because the
// callback overwrites it with the one-time code.
export const buildAppleReturnUrl = (href: string): string => {
  const url = new URL(href)
  url.hash = ''
  // Same origin the redirect URI resolves to. Off a preview deployment the raw
  // origin is one Apple never heard of and the callback will not return to it.
  return `${getAppleOrigin(url.origin)}${url.pathname}${url.search}`
}

// The state the Apple callback reads as redirect mode. Mirrors the string
// public/iframe.js builds for the embedded flow; parseAppleCallbackState splits it.
export const buildAppleRedirectState = (nonce: string, returnUrl: string) => `${nonce}|R|${toBase64Url(returnUrl)}`

// Errors Apple itself can return to the authorize endpoint
const APPLE_ERROR_CODES = [
  'user_cancelled_authorize',
  'invalid_request',
  'invalid_client',
  'invalid_scope',
  'invalid_grant',
  'unauthorized_client',
  'unsupported_response_type',
  'access_denied',
  'server_error',
  'temporarily_unavailable'
]

// Reduces an error arriving in the URL fragment to a known code. Anyone can put a
// fragment on a link to Connect, and the value ends up in the login dialog, in error reports
// and in analytics — so an unrecognised one becomes a fixed code rather than carrying
// attacker-written text into any of them.
export const toKnownAppleError = (value: string | null) => (value && APPLE_ERROR_CODES.includes(value) ? value : 'apple_auth_failed')

// Shown when a sign in cannot be completed and there is nothing the patient can do
// differently. Also covers a code that comes back but cannot be redeemed: an expired
// or already-used code, or a payload with no token.
export const APPLE_SIGN_IN_FAILED_MESSAGE = 'Your Apple sign in could not be completed. Please try again.'

const APPLE_UNAVAILABLE_MESSAGE = 'Apple sign in is temporarily unavailable. Please try again in a moment.'

// The codes worth telling a patient apart. Everything else falls back to the sentence
// above, including anything toKnownAppleError has already reduced to apple_auth_failed.
const APPLE_ERROR_MESSAGES: Record<string, string> = {
  server_error: APPLE_UNAVAILABLE_MESSAGE,
  temporarily_unavailable: APPLE_UNAVAILABLE_MESSAGE,
  access_denied: 'Apple sign in was not completed. Please try again or use another sign in method.'
}

// The banner renders the error's message, so a code reaching it has to read as a
// sentence rather than as server_error. hasOwn, because a plain lookup answers
// "constructor" and "toString" with an inherited function, which would render as
// its own source text.
export const toAppleErrorMessage = (code: string) => (Object.hasOwn(APPLE_ERROR_MESSAGES, code) ? APPLE_ERROR_MESSAGES[code] : APPLE_SIGN_IN_FAILED_MESSAGE)
